import { Locale as LocaleEnum } from "@redotech/cockroachdb-codegen/tables/common/locale";

/**
 * The locales the product supports. Derived from the `locale` CockroachDB enum
 * so the schema and the frontend cannot disagree: `Locale` is the enum widened
 * back to a string-literal union, which is what the i18next APIs, the locale
 * cookie, and `Accept-Language` parsing all hand us.
 *
 * Add a locale in redo/cockroachdb/schema/definitions/tables/common/locale.sql,
 * then run `bazel run refresh` and `bazel run //redo/scurry:schema_sync`.
 */
export const ALL_LOCALES: readonly Locale[] = Object.values(LocaleEnum);

export type Locale = `${LocaleEnum}`;

export const DEFAULT_LOCALE: Locale = "en";

/**
 * Get the display name for a locale using the Intl API. By default this is the
 * locale's native name (endonym) — e.g. "Français" for `fr` — which suits
 * shopper-facing pickers. Pass `displayLocale` (e.g. `"en"`) to render the name
 * in another language instead — e.g. "French" — for an English admin UI.
 *
 * Accepts any BCP-47 code (not just a known `Locale`) since it is fully
 * defensive — an unknown or malformed code falls back to the code itself.
 */
export function getLocaleName(
  locale: Locale | string,
  displayLocale: Locale | string = locale,
): string {
  try {
    const name =
      new Intl.DisplayNames([displayLocale], { type: "language" }).of(locale) ??
      locale;
    return name[0]?.toLocaleUpperCase(displayLocale) + name.slice(1);
  } catch {
    return locale;
  }
}

/**
 * Whether a locale matches a user search query, by display name or by code
 * (e.g. "fre" and "fr" both match French). The canonical matcher for any
 * locale picker UI.
 */
export function localeMatchesSearch(
  locale: Locale | string,
  query: string,
): boolean {
  const q = query.trim().toLocaleLowerCase();
  if (!q) {
    return true;
  }
  // Match on the native name, the English name, or the code, so the picker
  // finds a locale regardless of which name the UI happens to display.
  return (
    getLocaleName(locale).toLocaleLowerCase().includes(q) ||
    getLocaleName(locale, DEFAULT_LOCALE).toLocaleLowerCase().includes(q) ||
    locale.includes(q)
  );
}

/** Filter locale codes down to those matching a user search query. */
export function searchLocales<T extends Locale | string>(
  locales: readonly T[],
  query: string,
): readonly T[] {
  if (!query.trim()) {
    return locales;
  }
  return locales.filter((locale) => localeMatchesSearch(locale, query));
}
